import {
  Button,
  Form,
  InputFieldRhf,
  XStack,
  Error,
  CheckboxFieldRhf,
  TextLink,
  SizableText,
  YStack,
  Stack,
} from '@my/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { zodEmailValidator } from 'app/utils/zod-email-validator'

const formSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: 'First name is required' })
      .max(200, { message: 'First name must be less than 200 characters' }),
    lastName: z
      .string()
      .min(1, { message: 'Last name is required' })
      .max(200, { message: 'Last name must be less than 200 characters' }),
    email: z.string().refine(zodEmailValidator, { message: 'Invalid email' }),
    confirmEmail: z.string().refine(zodEmailValidator, { message: 'Invalid email' }),
    password: z.string().min(8, { message: 'Password must be at least 8 characters' }),
    terms: z
      .boolean()
      .refine((value) => value, { message: 'You must accept the terms and conditions' }),
  })
  .refine((data) => data.email === data.confirmEmail, {
    message: 'Emails must match',
    path: ['confirmEmail'],
  })

export type SignUpFormValues = z.infer<typeof formSchema>

export interface SignUpFormProps {
  loading: boolean
  error: string
  onSubmit: (values: SignUpFormValues) => void
}

export const SignUpForm = (props: SignUpFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      password: '',
      terms: false,
    },
  })

  return (
    <Form
      gap="$3"
      w="100%"
      onSubmit={handleSubmit((data) => {
        props.onSubmit(data)
      })}
    >
      <InputFieldRhf
        name="firstName"
        control={control}
        label="First Name"
        placeholder="First Name"
        hideLabel
        required
        autoComplete="name-given"
      />

      <InputFieldRhf
        name="lastName"
        control={control}
        label="Last Name"
        placeholder="Last Name"
        hideLabel
        required
        autoComplete="name-family"
      />

      <InputFieldRhf
        name="email"
        control={control}
        label="Email"
        placeholder="Personal Email Address"
        inputMode="email"
        hideLabel
        required
        autoComplete="email"
      />

      <InputFieldRhf
        name="confirmEmail"
        control={control}
        label="Email"
        placeholder="Confirm Personal Email Address"
        inputMode="email"
        hideLabel
        required
        autoComplete="email"
      />

      <InputFieldRhf
        name="password"
        control={control}
        label="Password"
        isPassword
        placeholder="Create Password"
        hideLabel
        required
        autoComplete="password"
        passwordIconProps={{ color: 'gray' }}
      />
      <Stack pt="$4">
        <CheckboxFieldRhf
          name="terms"
          control={control}
          size="$6"
          label={
            <SizableText color="$overlay11">
              <SizableText color="$overlay11" fow="600">
                I agree
              </SizableText>{' '}
              - to the{' '}
              <TextLink color="$overlay11" pl="$2" href="/terms-conditions">
                terms and conditions
              </TextLink>
            </SizableText>
          }
          required
        />
      </Stack>

      {(errors.firstName?.message ||
        errors.lastName?.message ||
        errors.email?.message ||
        errors.confirmEmail?.message ||
        errors.password?.message ||
        errors.terms?.message) && (
        <YStack px="$3" py="$2" borderRadius={4} borderWidth={1} borderColor="$red11">
          {errors.firstName?.message && <Error error={errors.firstName.message}></Error>}
          {errors.lastName?.message && <Error error={errors.lastName.message}></Error>}
          {errors.email?.message && <Error error={errors.email.message}></Error>}
          {errors.confirmEmail?.message && <Error error={errors.confirmEmail.message}></Error>}
          {errors.password?.message && <Error error={errors.password.message}></Error>}
          {errors.terms?.message && <Error error={errors.terms.message}></Error>}
        </YStack>
      )}
      {props.error && (
        <YStack px="$3" py="$2" borderRadius={4} borderWidth={1} borderColor="$red11">
          <Error error={props.error}></Error>
        </YStack>
      )}
      <XStack pt="$4" w="100%" gap="$3">
        <Form.Trigger asChild fd="row" ai="center">
          <Button
            plain
            backgroundColor="$primary"
            hoverStyle={{ backgroundColor: '$primary4' }}
            fontSize="$5"
            w="100%"
            loading={props.loading}
          >
            Join Now!
          </Button>
        </Form.Trigger>
      </XStack>
    </Form>
  )
}
